<template>
  <FormsProvider v-bind="formsConfig" class="cn-page-blocks">
    <template v-for="(component, i) in preparedComponents">
      <AbSectionWidget
        v-if="component.isAsbisUi"
        :key="`ab-${i}`"
        :section-name="component.type"
        :data="component.data"
        :locale="locale"
        :meta-info="metaInfo"
      />
      <component
        :is="kebabToPascal(component.type)"
        v-else
        :key="`local-${i}`"
        v-bind="component.data"
      />
    </template>
  </FormsProvider>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { sections } from '@webplatform/asbis-ui/sections';
import { FormsProvider } from '@webplatform/asbis-ui';
import type { FormMetaInfo } from '@webplatform/asbis-ui';
import { AbSectionWidget } from '@webplatform/asbis-ui/integrations/nuxt';
import { prepareDynamicBlock, kebabToPascal } from '~/helpers';
import type {
  PreparedBlock,
  RawBlock,
} from '~/domains/constructor-pages/types';

type EnhancedBlock = PreparedBlock & { isAsbisUi: boolean };

const COMPONENTS_ALIASES: Record<string, string> = {
  'html-content-block': 'prose-block',
  red_block: 'rich-header',
  gallery: 'rich-images-grid',
  chess_gallery: 'rich-advantages-list',
  youtube_video: 'rich-video',
  footer: 'rich-footer',
  'tips-section': 'tips-section',
};

const EXCLUDED_BLOCKS = ['page-info', 'short_description'];

const LOCAL_SECTIONS_WITH_DATA_PROPS = [
  'HtmlContentBlock',
  'Red_block',
  'Gallery',
  'Chess_gallery',
  'Youtube_video',
  'Footer',
  'AboutCards',
  'AboutGaming',
  'AboutHeader',
  'AboutImage',
  'AboutText',
  'AppPromotions',
  'CategoryBannerLarge',
  'CategoryBannerSmall',
  'CategoryBannerSmallGroup',
  'CategoryCardsSection',
  'FormSection',
  'MainSlider',
  'NewsSection',
  'ProductCharacteristics',
  'ProseBlock',
  'RichAdvantages',
  'RichAdvantagesList',
  'RichFooter',
  'RichHeader',
  'RichImagesGrid',
  'RichVideo',
  'SeoText',
  'TipsSection',
  'UserHelpCards',
];

const isAsbisUiRawBlock = (block: any): boolean => {
  const sectionType = block?.content?.type ?? block?.type;
  return kebabToPascal(sectionType) in sections;
};

const props = defineProps({
  blocks: {
    type: Array as () => RawBlock[],
    required: true,
  },
  metaInfo: {
    type: Object as () => FormMetaInfo,
    default: () => ({}),
  },
});

const config = useRuntimeConfig().public.frontkit;
const { locale } = useI18n();

const formsConfig = {
  baseUrl: config.baseUrl,
  locale: locale.value,
};

const preparedComponents = computed<EnhancedBlock[]>(() =>
  ((props.blocks as any[]) || [])
    .map((block) => ({
      isAsbisUi: isAsbisUiRawBlock(block),
      ...prepareDynamicBlock(block as RawBlock, {
        excludedBlocks: EXCLUDED_BLOCKS,
        aliases: COMPONENTS_ALIASES,
        sectionsWithDataProps: LOCAL_SECTIONS_WITH_DATA_PROPS,
      }),
    }))
    .filter((c): c is EnhancedBlock => !!c?.type),
);
</script>
